import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AnySchema, object as yupObject } from 'yup'
import {
  ComboboxGroupItem,
  ComboboxGroupProps,
  ComboboxItem,
  ComboboxProps,
} from '@node-space/ui-components'
import { FieldMaxLength } from 'constants/forms'
import { DateFormat } from 'constants/General'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useYupFields } from 'hooks/forms/useYupFields'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useCountryStatesQuery } from 'hooks/queries/Customers/useCountryStates'
import { useIndustryOptions } from 'hooks/queries/Customers/useIndustryOptions'
import { useMonthlyVolumesQuery } from 'hooks/queries/Customers/useMonthlyVolumesQuery'
import { useAllowedCountriesQuery } from 'hooks/queries/useAllowedCountriesQuery'
import { CountryCodes, type ErrorDetails } from 'types/types'
import { getFormattedSingleDateValue } from 'utils/date'
import { getCountryOptions } from 'utils/forms'
import {
  BASE_CUSTOMER_OPTIONS,
  BUSINESS_CUSTOMER_LABEL_MAPPING,
  mapBusinessErrorKeyToFormKey,
  setFieldErrors,
} from '../helpers/form'
import {
  BUSINESS_COMBOBOX_GROUP_KEYS,
  BUSINESS_COMBOBOX_KEYS,
  BUSINESS_RADIO_KEYS,
  BusinessComboboxGroupKey,
  BusinessComboboxKey,
  BusinessCustomerFormData,
  BusinessCustomerFormFields,
  BusinessCustomerFormKey,
  BusinessCustomerFormSchema,
  UseCase,
  type BusinessRadioKey,
} from '../helpers/types'

const isBusinessComboboxKey = (key: BusinessCustomerFormKey): key is BusinessComboboxKey => {
  return BUSINESS_COMBOBOX_KEYS.includes(key as BusinessComboboxKey)
}

const isBusinessComboboxGroupKey = (
  key: BusinessCustomerFormKey
): key is BusinessComboboxGroupKey => {
  return BUSINESS_COMBOBOX_GROUP_KEYS.includes(key as BusinessComboboxGroupKey)
}

const isBusinessRadioKey = (key: BusinessCustomerFormKey): key is BusinessRadioKey => {
  return BUSINESS_RADIO_KEYS.includes(key as BusinessRadioKey)
}

const PAXOS_FIELD_KEYS: BusinessCustomerFormKey[] = [
  'isRegulated',
  'regulatorName',
  'regulatorJurisdiction',
  'isPubliclyListed',
  'listedExchange',
  'tickerSymbol',
]

const OPERATIONAL_ADDRESS_KEYS: BusinessCustomerFormKey[] = [
  'operationalAddressLine1',
  'operationalAddressLine2',
  'operationalAddressCity',
  'operationalAddressPostalCode',
  'operationalAddressCountryCode',
  'operationalAddressStateCode',
]

export const useBusinessCustomerForm = (
  defaultValues: BusinessCustomerFormData,
  fieldErrors?: ErrorDetails,
  isReliance?: boolean,
  paxosUsStates?: string[]
) => {
  const { t } = useTranslation()
  const { getConditionalYupField, getRequiredYupField, getYupField } = useYupFields()
  const [selectedCountry, setSelectedCountry] = useState<string>(
    defaultValues?.countryOfIncorporation
  )
  const {
    data: allowedCountries = [],
    isFetching: isFetchingAllowedCountries,
    isError: isErrorFetchingAllowedCountries,
  } = useAllowedCountriesQuery()

  const {
    industryOptions,
    isPending: isFetchingIndustries,
    error: isErrorFetchingIndustries,
  } = useIndustryOptions()

  const {
    data: monthlyVolumes,
    isFetching: isFetchingMonthlyVolumes,
    isError: isErrorFetchingMonthlyVolumes,
  } = useMonthlyVolumesQuery()

  const {
    data: countryStates = [],
    isFetching: isFetchingCountryStates,
    isError: isErrorFetchingCountryStates,
  } = useCountryStatesQuery(selectedCountry, selectedCountry === CountryCodes?.US)

  const comboboxItems: Partial<Record<BusinessCustomerFormKey, ComboboxItem[]>> = {
    ...BASE_CUSTOMER_OPTIONS,
    countryOfIncorporation: getCountryOptions(allowedCountries),
    monthlyExpectedVolumes: monthlyVolumes?.map(data => ({
      leftText: data.name,
      value: data.reference,
    })),
    stateCode: countryStates,
    regulatorJurisdiction: getCountryOptions(allowedCountries),
    operationalAddressCountryCode: getCountryOptions(allowedCountries),
    operationalAddressStateCode: countryStates,
  }

  const comboboxGroupItems: Partial<Record<BusinessCustomerFormKey, ComboboxGroupItem[]>> = {
    industry: industryOptions,
  }

  const isComboboxLoading: Partial<Record<BusinessCustomerFormKey, boolean>> = {
    countryOfIncorporation: isFetchingAllowedCountries,
    monthlyExpectedVolumes: isFetchingMonthlyVolumes,
    industry: isFetchingIndustries,
    stateCode: isFetchingCountryStates,
    regulatorJurisdiction: isFetchingAllowedCountries,
    operationalAddressCountryCode: isFetchingAllowedCountries,
    operationalAddressStateCode: isFetchingCountryStates,
  }

  const isErrorLoadingOptions: Partial<Record<BusinessCustomerFormKey, boolean>> = {
    countryOfIncorporation: isErrorFetchingAllowedCountries,
    industry: isErrorFetchingIndustries,
    monthlyExpectedVolumes: isErrorFetchingMonthlyVolumes,
    stateCode: isErrorFetchingCountryStates,
    regulatorJurisdiction: isErrorFetchingAllowedCountries,
    operationalAddressCountryCode: isErrorFetchingAllowedCountries,
    operationalAddressStateCode: isErrorFetchingCountryStates,
  }

  const optionErrorFields = Object.keys(isErrorLoadingOptions)?.filter(
    key => isErrorLoadingOptions?.[key as BusinessCustomerFormKey]
  )

  const effectiveDefaultValues: BusinessCustomerFormData = (() => {
    return defaultValues
  })()

  const formKeys = Object.keys(effectiveDefaultValues) as BusinessCustomerFormKey[]

  const isPaxosState = (value: string): boolean => {
    if (!value || !Array.isArray(paxosUsStates) || !paxosUsStates?.length) return false
    return paxosUsStates?.includes(value)
  }

  const getFieldSchema = (key: BusinessCustomerFormKey): AnySchema => {
    const label = t(
      BUSINESS_CUSTOMER_LABEL_MAPPING?.[key] ?? `embeddedCustomers.businessForm.${key}`
    )
    const fieldSchema = getRequiredYupField({ label })
    switch (key) {
      case 'businessDescription':
        return fieldSchema?.max(FieldMaxLength.LONG, t('formFields.errors.fieldTooLong'))
      case 'externalReference':
      case 'addressLine2':
        return getYupField({ label })

      case 'taxNumber':
      case 'stateCode':
        return getConditionalYupField<BusinessCustomerFormKey>({
          label,
          fieldKey: 'countryOfIncorporation',
          fieldValue: CountryCodes.US,
        })
      case 'registrationNumber':
        return getConditionalYupField<BusinessCustomerFormKey>({
          label,
          fieldKey: 'countryOfIncorporation',
          fieldValue: value => !!value && value !== CountryCodes.US,
        })
      case 'isRegulated':
      case 'isPubliclyListed':
        return getConditionalYupField<BusinessCustomerFormKey>({
          label,
          fieldKey: 'stateCode',
          fieldValue: isPaxosState,
        })
      case 'regulatorName':
      case 'regulatorJurisdiction':
        return getConditionalYupField<BusinessCustomerFormKey>({
          label,
          fieldKey: 'isRegulated',
          fieldValue: 'true',
        })
      case 'listedExchange':
      case 'tickerSymbol':
        return getConditionalYupField<BusinessCustomerFormKey>({
          label,
          fieldKey: 'isPubliclyListed',
          fieldValue: 'true',
        })
      case 'operationalAddressLine2':
        return getYupField({ label })
      case 'operationalAddressLine1':
      case 'operationalAddressCity':
      case 'operationalAddressStateCode':
      case 'operationalAddressPostalCode':
      case 'operationalAddressCountryCode':
        return getConditionalYupField<BusinessCustomerFormKey>({
          label,
          fieldKey: 'isOperationalAddressDifferent',
          fieldValue: 'true',
        })

      default:
        return fieldSchema
    }
  }

  const formSchema = yupObject(
    formKeys?.reduce((acc, key) => {
      acc[key] = getFieldSchema(key)
      return acc
    }, {} as BusinessCustomerFormSchema)
  )
  const form = useForm<BusinessCustomerFormData>({
    defaultValues: effectiveDefaultValues,
    mode: 'onChange',
    resolver: yupFormResolver<BusinessCustomerFormData>(formSchema),
  })
  const { handleSubmit, setValue, setError, clearErrors, control, getValues } = form
  const formValues = useWatch({ control })
  const previousCountryRef = useRef(formValues?.countryOfIncorporation)
  const hasErrors = !!Object.keys(form?.formState?.errors)?.length

  const { setInputField, setComboboxField, setSingleDateField } =
    useFormFields<BusinessCustomerFormKey>(formSchema, form, formValues)

  const getFormField = (key: BusinessCustomerFormKey) => {
    if (isBusinessComboboxKey(key)) {
      const comboboxField: ComboboxProps = {
        ...(setComboboxField({ key, items: comboboxItems?.[key] ?? [] }) as ComboboxProps),
        loadingInline: isComboboxLoading?.[key],
        defaultValue: formValues?.[key],
      }
      return comboboxField
    }

    if (isBusinessComboboxGroupKey(key)) {
      const comboboxGroupField: ComboboxGroupProps = {
        ...(setComboboxField({
          key,
          items: comboboxGroupItems?.[key] ?? [],
        }) as ComboboxGroupProps),
        loadingInline: isComboboxLoading?.[key],
        defaultValue: formValues?.[key],
      }
      return comboboxGroupField
    }

    if (key === 'businessOperationsStartDate') {
      const field = setSingleDateField(key)
      return {
        ...field,
        labels: {
          ...field.labels,
          date: t('embeddedCustomers.businessForm.businessOperationsStartDate'),
        },
      }
    }

    return {
      ...setInputField({ key }),
      onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
        setValue(key, event?.target?.value as never, { shouldValidate: true }),
    }
  }

  const formFields = formKeys?.reduce((acc, key) => {
    if (isBusinessRadioKey(key)) return acc
    const accRecord = acc as unknown as Record<BusinessCustomerFormKey, unknown>
    accRecord[key] = getFormField(key)
    return acc
  }, {} as BusinessCustomerFormFields)

  const getComboboxLabel = <LabelType extends string>(key: BusinessCustomerFormKey) => {
    return comboboxItems?.[key]?.find(({ value }) => value === formValues?.[key])
      ?.leftText as LabelType
  }

  const getComboboxGroupLabel = (key: BusinessCustomerFormKey): string => {
    const groups = comboboxGroupItems?.[key] ?? []
    for (const group of groups) {
      const match = group.items?.find(({ value }) => value === formValues?.[key])
      if (match) return match?.label
    }
    return ''
  }

  const getBooleanDisplayValue = (value?: string): string => {
    if (value === 'true') return t('yes')
    if (value === 'false') return t('no')
    return ''
  }

  const displayData: Partial<BusinessCustomerFormData> = {
    businessOperationsStartDate: getFormattedSingleDateValue(
      formValues?.businessOperationsStartDate ?? '',
      DateFormat.DD_MMMM_YYYY
    ),
    industry: getComboboxGroupLabel('industry'),
    countryOfIncorporation: getComboboxLabel('countryOfIncorporation'),
    monthlyExpectedVolumes: getComboboxLabel('monthlyExpectedVolumes'),
    useCase: getComboboxLabel<UseCase>('useCase'),
    stateCode: getComboboxLabel('stateCode'),
    regulatorJurisdiction: getComboboxLabel('regulatorJurisdiction'),
    isRegulated: getBooleanDisplayValue(formValues?.isRegulated),
    isPubliclyListed: getBooleanDisplayValue(formValues?.isPubliclyListed),
    isOperationalAddressDifferent: getBooleanDisplayValue(
      formValues?.isOperationalAddressDifferent
    ),
    operationalAddressCountryCode: getComboboxLabel('operationalAddressCountryCode'),
    operationalAddressStateCode: getComboboxLabel('operationalAddressStateCode'),
  }

  const showPaxosUsStatesFields = useMemo(() => {
    if (!paxosUsStates?.length) return false
    if (formValues?.isOperationalAddressDifferent === 'true') {
      return (
        formValues?.operationalAddressCountryCode === CountryCodes.US &&
        !!formValues?.operationalAddressStateCode &&
        paxosUsStates.includes(formValues.operationalAddressStateCode)
      )
    }
    return (
      formValues?.countryOfIncorporation === CountryCodes.US &&
      !!formValues?.stateCode &&
      paxosUsStates.includes(formValues.stateCode)
    )
  }, [
    formValues?.isOperationalAddressDifferent,
    formValues?.operationalAddressCountryCode,
    formValues?.operationalAddressStateCode,
    formValues?.countryOfIncorporation,
    formValues?.stateCode,
    paxosUsStates,
  ])

  const showOperationalAddress = formValues.isOperationalAddressDifferent === 'true'

  const previousShowOperationalAddressRef = useRef(showOperationalAddress)

  useEffect(() => {
    if (previousShowOperationalAddressRef.current && !showOperationalAddress) {
      OPERATIONAL_ADDRESS_KEYS.forEach(field => {
        if (getValues(field)) {
          setValue(field, '' as never, { shouldDirty: true, shouldValidate: true })
        }
      })
      clearErrors(OPERATIONAL_ADDRESS_KEYS)
    }
    previousShowOperationalAddressRef.current = showOperationalAddress
  }, [showOperationalAddress, getValues, setValue, clearErrors])

  const previousShowPaxosRef = useRef(showPaxosUsStatesFields)

  useEffect(() => {
    if (previousShowPaxosRef.current && !showPaxosUsStatesFields) {
      PAXOS_FIELD_KEYS.forEach(field => {
        if (getValues(field)) {
          setValue(field, '' as never, { shouldDirty: true, shouldValidate: true })
        }
      })
    }
    previousShowPaxosRef.current = showPaxosUsStatesFields
  }, [showPaxosUsStatesFields, getValues, setValue])

  useEffect(() => {
    const currentCountry = formValues?.countryOfIncorporation
    const previousCountry = previousCountryRef.current

    if (!currentCountry || currentCountry === previousCountry) return

    setSelectedCountry(currentCountry)

    if (previousCountry && currentCountry !== CountryCodes.US) {
      const stateValue = getValues('stateCode')
      if (stateValue) {
        setValue('stateCode', '', { shouldDirty: true, shouldValidate: true })
      }
    }

    const fieldToClear = currentCountry === CountryCodes.US ? 'registrationNumber' : 'taxNumber'
    const fieldValue = getValues(fieldToClear)

    if (fieldValue) {
      setValue(fieldToClear, '', { shouldDirty: true, shouldValidate: true })
    }

    previousCountryRef.current = currentCountry
  }, [formValues?.countryOfIncorporation, getValues, isReliance, setValue])

  const appliedFieldErrorsRef = useRef<ErrorDetails | undefined>(undefined)

  useEffect(() => {
    if (fieldErrors && fieldErrors !== appliedFieldErrorsRef.current) {
      setFieldErrors(fieldErrors, mapBusinessErrorKeyToFormKey, setError)
      appliedFieldErrorsRef.current = fieldErrors
    }
  }, [fieldErrors, setError])

  useEffect(() => {
    if (optionErrorFields?.length) {
      optionErrorFields.forEach(key => {
        setError(key as BusinessCustomerFormKey, {
          type: 'disabled',
          message: t('formFields.errors.loadingOptions'),
        })
      })
    }
  }, [optionErrorFields?.length, setError, t])

  return {
    formFields,
    displayData,
    hasErrors,
    handleSubmit,
    form,
    formValues,
    setValue,
    showPaxosUsStatesFields,
    showOperationalAddress,
  }
}
